import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable } from 'rxjs';

import Table, { TableColumn } from '../../../../components/common/table/table';
import Form, { SubmitType } from '../../../../components/form';
import Button from '../../../../components/form/elements/button';
import Checkbox from '../../../../components/form/elements/checkbox';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import { default as DateComp } from '../../../../components/form/elements/date';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Decimal from '../../../../components/form/elements/decimal';
import Readonly from '../../../../components/form/elements/readonly';
import Select, { Option } from '../../../../components/form/elements/select';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import TextArea from '../../../../components/form/elements/text-area';
import { CropWorkType } from '../../../../enums/crop-work-types';
import Field from '../../../../interfaces/field';
import Paginate from '../../../../interfaces/paginate';
import Work from '../../../../interfaces/work';
import { CROP_FIELDS_AND_WORKS_INDEX } from '../../../../router/routes';
import {
  findNewestSowingBeforeOtherWorks,
  can,
  route,
} from '../../../../services/utils';
import { FormData } from './create';
import { Section } from '../index';
import { useParams } from 'react-router-dom';
import css from './step-overview.module.css';
import File from '../../../../components/form/elements/file';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useBackButton } from '../backButtonContext';

export default function StepOverview({
  formData,
  onReturn,
  onSubmit,
  onEdit,
  onDelete,
}: {
  formData: FormData;
  onReturn?: () => void;
  onSubmit?: SubmitType<null, Work>;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const { farmId, season } = useParams<{ farmId: string; season: string }>();

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  const { backButton } = useBackButton();

  const chooseCategoryForSpraying = Object.values(
    formData.work.work.properties
  ).find(
    (property) =>
      property.property.key ===
      ClassifierPropertyKey.CHOOSE_CATEGORY_FOR_SPRAYING
  ).value;

  const requestFields = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) => {
      const fields = formData.fields
        ?.filter((field) => field?.field?.field)
        .map((field) => {
          const treatedAreaString = field.treated_area.toString();
          const treatedArea = parseFloat(
            treatedAreaString.replace(/(\.[0-9]*[1-9])0*|(\.0*)/, '$1')
          );

          return {
            id: field.field.value,
            lad_number: field.field.field.lad_number,
            name: field.field.label,
            area: field.field.field.area,
            // crop_value: if in create form, than there is no pivot record in db, so we take field's crop. If edit form, then
            // field may not have crop already and we take crop from pivot table
            crop_value: field.field.field.work_crop?.value
              ? field.field.field.work_crop?.value
              : field.crop_value,
            plant_development_stage: field.plant_development_stage?.label,
            treated_area: treatedArea,
          };
        });
      return new Observable<Paginate<any>>((subscriber) =>
        subscriber.next({
          data: fields,
        } as Paginate<any>)
      );
    },
    [formData]
  );

  const requestProducts = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) => {
      const products = formData.products
        ?.filter((product) => product?.product?.value)
        .map((product) => {
          return {
            id: product.product.value,
            crop_group: product.crop_group?.label,
            product_type: product.product_type?.label,
            product_category: product.product_category?.label,
            product: product.product.label,
            reason_of_use: product.reason_of_use,
            warehouses: product.warehouses,
          };
        });
      return new Observable<Paginate<any>>((subscriber) =>
        subscriber.next({
          data: products,
        } as Paginate<any>)
      );
    },
    [formData]
  );

  const requestIntercrop = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) => {
      const products = formData.intercrop
        ?.filter((product) => product?.product?.value)
        .map((product) => {
          return {
            id: product.product.value,
            crop_group: product.crop_group?.label,
            product_type: product.product_type?.label,
            product_category: product.product_category?.label,
            product: product.product.label,
            warehouses: product.warehouses,
          };
        });
      return new Observable<Paginate<any>>((subscriber) =>
        subscriber.next({
          data: products,
        } as Paginate<any>)
      );
    },
    [formData]
  );

  const productWarehouses = (row: any) => {
    return (
      <table className={css['warehouses']}>
        <thead>
          <tr>
            <th>{tp('warehouse')}</th>
            <th>{tp('quantity')}</th>
          </tr>
        </thead>
        <tbody>
          {row.warehouses?.map((warehouse: any) => {
            if (!warehouse?.warehouse) {
              return null;
            } else {
              return (
                <tr key={warehouse.warehouse.value}>
                  <td>{warehouse.warehouse.label}</td>
                  <td>
                    {warehouse.quantity} {warehouse.quantity_unit.label}
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    );
  };

  let productTableColumns = [] as TableColumn<any>[];
  if (formData.type_id === CropWorkType.SOWING_OR_PLANTING) {
    productTableColumns.push({
      accessor: 'crop_group',
      header: tp('crop_group'),
    });
  }
  productTableColumns.push({
    accessor: 'product_category',
    header: tp('product_category'),
  });
  if (formData.type_id !== CropWorkType.SOWING_OR_PLANTING) {
    productTableColumns.push({
      accessor: 'product_type',
      header: tp('product_type'),
    });
  }
  productTableColumns.push({
    accessor: 'product',
    header: tp('product'),
  });

  if (
    formData.type_id === CropWorkType.DISPERSION_OR_SPRAYING &&
    chooseCategoryForSpraying
  ) {
    productTableColumns.push({
      accessor: 'reason_of_use',
      header: tp('reason_of_use'),
    });
  }

  let fieldTableColumns = [
    {
      accessor: 'lad_number',
      header: t('pages.crop.fields_and_works.fields.lad_number'),
    },
    {
      accessor: 'name',
      header: t('pages.crop.fields_and_works.fields.name'),
    },
    {
      accessor: 'area',
      header: t('pages.crop.fields_and_works.fields.area'),
    },
    {
      accessor: 'treated_area',
      header: t('pages.crop.fields_and_works.fields.treated_area'),
    },
    {
      accessor: 'crop_value',
      header: t('pages.crop.fields_and_works.fields.crop'),
    },
  ] as TableColumn<any>[];

  if (formData.type_id === CropWorkType.DISPERSION_OR_SPRAYING) {
    fieldTableColumns.push({
      accessor: 'plant_development_stage',
      header: tp('plant_development_stage'),
    });
  }

  const [body] = useState(() => [
    Group([
      Readonly([
        Text('type_name', tp('type')),
        Select('work', { label: tp('work') }),
        Show(
          Text('name', tp('name')),
          'work',
          (work: Option) => work?.label === 'Cits'
        ),
        DateComp('date', tp('date')),
        Select('employees', { label: tp('employees'), multi: true }),
        Select('equipment', { label: tp('equipment'), multi: true }),
        TextArea('notes', tp('notes')),
        File('files', tp('files'), true),
        Decimal('duration', tp('duration')),
        Checkbox('done', tp('done')),
      ]),
    ]),
    () => (
      <div className={css['fields']}>
        <h3>{tp('fields')}</h3>
        <Table<Field>
          request={requestFields}
          keyAccessor="id"
          columns={fieldTableColumns}
          paginate={false}
        />
      </div>
    ),
    () => {
      if (
        !formData.products?.filter((product) => product?.product?.value).length
      ) {
        return null;
      }
      return (
        <div className={css['products']}>
          <h3>{tp('products.title')}</h3>
          <Table<any>
            request={requestProducts}
            keyAccessor="id"
            columns={productTableColumns}
            paginate={false}
            rowExpander={productWarehouses}
          />
        </div>
      );
    },
    () => {
      if (
        !formData.intercrop?.filter((product) => product.product?.value).length
      ) {
        return null;
      }
      return (
        <div className={css['products']}>
          <h3>{tp('intercrop.title')}</h3>
          <Table<any>
            request={requestIntercrop}
            keyAccessor="id"
            columns={productTableColumns}
            paginate={false}
            rowExpander={productWarehouses}
          />
        </div>
      );
    },
  ]);

  const [footer] = useState(() => {
    const canCreateWork = can(farmUser, { title: 'create work' });
    const canUpdateWork = can(farmUser, { title: 'update work' });
    const canDeleteWork = can(farmUser, { title: 'delete work' });

    if (!canCreateWork && !canUpdateWork && !canDeleteWork) {
      return [];
    }

    if (onEdit || onDelete) {
      const buttons = [];
      if (onEdit && canUpdateWork) {
        buttons.push(Button('edit', onEdit));
      }
      const newestSowingBeforeOtherWorks =
        findNewestSowingBeforeOtherWorks(formData);

      if (
        onDelete &&
        canDeleteWork &&
        !newestSowingBeforeOtherWorks.some((work) => work.id === formData.id)
      ) {
        buttons.push(Button('delete', onDelete));
      }

      return buttons;
    } else {
      return [
        Button(
          'return',
          onReturn,
          'pages.crop.works.return',
          faLongArrowLeft,
          null,
          true
        ),
        Link(
          route(CROP_FIELDS_AND_WORKS_INDEX, {
            farmId: farmId,
            season: season,
            tab: Section.fields,
          }),
          'cancel'
        ),
        Button(formData.id ? 'save' : 'create'),
      ];
    }
  });

  return (
    <div>
      <Form
        title={onEdit ? tp('view') : formData.id ? tp('edit') : tp('create')}
        mode={onEdit ? 'view' : formData.id ? 'edit' : 'create'}
        backTo={
          onEdit
            ? backButton ||
              route(CROP_FIELDS_AND_WORKS_INDEX, {
                farmId: farmId,
                season: season,
                tab: Section.workOverview,
              })
            : null
        }
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
      />
    </div>
  );
}
